import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import service from "../../../services/service";
import { useParameters } from "../../../context/parametersContext";
import "../../../styles/index.less";
import { useAuth } from "../../../context/authContext";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../shared/LoadingOverlay/LoadingOverlay";
import {
  formatMarketPrice,
  safeFormat,
  toWholeNumber,
} from "../../../utils/financialHelper";
// Toastify styles loaded via main stylesheet

const reitImages: Record<string, string> = {
  Embassy: new URL("../../../assets/Embassy_1.png", import.meta.url).href,
  Mindspace: new URL("../../../assets/Mindspace_4.png", import.meta.url).href,
  Brookfield: new URL("../../../assets/Brookfield_2.png", import.meta.url).href,
  Nexus: new URL("../../../assets/Nexus_2.png", import.meta.url).href,
};
const reitcard1 = new URL("../../../assets/reitCard1.png", import.meta.url)
  .href;
const reitcard2 = new URL("../../../assets/reitCard2.png", import.meta.url)
  .href;
const compareArrow = new URL(
  "../../../assets/compare-arrow.png",
  import.meta.url
).href;

export default function ReitsListing() {
  const navigate = useNavigate();

  const handleCompareClick = (reitId?: string) => {
    navigate("/reits/compare-properties", { state: { reitId } });
  };

  const handleArrowClick = () => {
    const idsArray = reitSummary.map((reit) => reit.id);
    navigate("/reits/compare", { state: { idsArray } });
  };

  const { reitSummary, setReitSummary, isLoading, setIsLoading } =
    useParameters();
  const [expandedReits, setExpandedReits] = useState<{
    [key: string]: boolean;
  }>({});
  const { isAuthenticated } = useAuth();

  const handleProtectedClick = () => {
    if (!isAuthenticated) {
      toast.error("You need to login first!");
      return false;
    }
    return true;
  };

  useEffect(() => {
    const fetchReits = async () => {
      setIsLoading(true);
      try {
        const data = await service.getReits();

        data.sort((a: any, b: any) => a.name.localeCompare(b.name));

        setReitSummary(data);
      } catch (error) {
        console.error("Failed to load REITs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (reitSummary.length === 0) {
      fetchReits();
    }
  }, [reitSummary.length, setReitSummary, setIsLoading]);

  const toggleExpand = (id: string) => {
    setExpandedReits((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (isLoading) {
    return (
      <div className="reits-page">
        <LoadingOverlay
          message="Loading REITs"
          overlay={false}
          isVisible={true}
        />
      </div>
    );
  }

  return (
    <div className="reits-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-text">
          <h1>
            Discover, Compare, and Value REITs{" "}
            <span>— for Smarter Investing.</span>
          </h1>
          <p>
            Use our intuitive comparison tool to analyze assets, returns, and
            operational performance — and pick the one that fits your financial
            goals.
          </p>
        </div>
      </section>

      {/* Search + Filter */}
      <div className="reits-header">
        <div className="reits-header__left">
          <h3>
            Quickly Compare REITs and <br />
            <span>Unlock Key Insights</span>
          </h3>
        </div>
        {/* To Do */}
        {/* <div className="reits-header__right">
          <input type="text" placeholder="Search" className="reits-search" />
          <button className="reits-filter">Filter by City</button>
        </div> */}
      </div>

      {/* REITs Cards */}
      <div className="reit-cards">
        {reitSummary.map((reit, index) => {
          const isExpanded = expandedReits[reit.id];
          const visibleLocations = isExpanded
            ? reit.location
            : reit.location.slice(0, 5);

          return (
            <div key={reit.id} className="reit-card">
              {/* Left image */}
              <img
                src={reitImages[reit.name.split(" ")[0]]}
                alt={reit.name}
                className="reit-banner"
              />

              {/* Right content */}
              <div className="reit-content">
                {/* Title row */}
                <div className="reit-content__header">
                  <div className="reit-content__title-block">
                    <h3 className="reit-content__title">{reit.name}</h3>
                  </div>
                  <div className="reit-content__meta">
                    <p className="reit-content__listing-date">
                      Listing on :{" "}
                      {new Date(reit.date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                    <button
                      className="reit-content__compare-btn"
                      onClick={() => {
                        if (handleProtectedClick()) {
                          handleCompareClick(reit.id);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                    >
                      <span className="reit-content__compare-btn-text">
                        Compare
                      </span>
                      <span className="reit-content__compare-btn-icon">
                        <img src={compareArrow} alt="compareArrow" />
                      </span>
                    </button>
                  </div>
                </div>

                {/* Locations */}
                {/* Locations */}
                <div className="reit-locations">
                  {visibleLocations.map((city, idx) => (
                    <span key={city}>
                      {city}
                      {idx < visibleLocations.length - 1 ? ", " : ""}
                      {idx === visibleLocations.length - 1 &&
                        reit.location.length > 5 && (
                          <span
                            className="more-toggle"
                            onClick={() => toggleExpand(reit.id)}
                          >
                            {isExpanded ? "–" : "+"}
                          </span>
                        )}
                    </span>
                  ))}
                </div>

                {/* Stats row + buttons */}
                <div className="reit-stats-row">
                  <div className="stat-block">
                    <span>
                      BSE <span className="tag">Prev Close</span>
                    </span>
                    <p>
                      {formatMarketPrice(reit.exchanges[0]?.openPrice)}{" "}
                      <span
                        className={`change ${
                          Number(reit.exchanges[0]?.openPriceChange) >= 0
                            ? "change--positive"
                            : "change--negative"
                        }`}
                      >
                        (
                        {Number(reit.exchanges[0]?.openPriceChange) > 0
                          ? `+${formatMarketPrice(
                              Number(reit.exchanges[0]?.openPriceChange)
                            )}`
                          : formatMarketPrice(
                              Number(reit.exchanges[0]?.openPriceChange)
                            )}
                        )
                      </span>
                    </p>
                  </div>

                  <div className="stat-block">
                    <span>
                      NSE <span className="tag">Prev Close</span>
                    </span>
                    <p>
                      {formatMarketPrice(reit.exchanges[1]?.openPrice)}{" "}
                      <span
                        className={`change ${
                          Number(reit.exchanges[1]?.openPriceChange) >= 0
                            ? "change--positive"
                            : "change--negative"
                        }`}
                      >
                        (
                        {Number(reit.exchanges[1]?.openPriceChange) > 0
                          ? `+${formatMarketPrice(
                              Number(reit.exchanges[1]?.openPriceChange)
                            )}`
                          : formatMarketPrice(
                              Number(reit.exchanges[1]?.openPriceChange)
                            )}
                        )
                      </span>
                    </p>
                  </div>

                  <div className="stat-block">
                    <span>{reit.portfolio.key}</span>
                    <p className="portfolio-value">
                      {safeFormat(reit.portfolio.value)}
                    </p>
                  </div>
                  <div className="stat-block">
                    <span>{reit.marketcap.key} (Approx)</span>
                    <p>{toWholeNumber(reit.marketcap.value)}</p>
                  </div>

                  {/* Buttons */}
                  <div className="reit-actions">
                    <NavLink
                      to={isAuthenticated ? `/reits/${reit.id}` : "#"}
                      onClick={(e) => {
                        if (!isAuthenticated) {
                          e.preventDefault(); // Prevent navigation
                          toast.error("You need to login first!");
                        } else {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                    >
                      <button className="btn-secondary">Know More</button>
                    </NavLink>
                    <NavLink
                      to={isAuthenticated ? `/reits/${reit.id}/valuation` : "#"}
                      state={{ reitId: reit.id, locations: reit.location }}
                      onClick={(e) => {
                        if (!isAuthenticated) {
                          e.preventDefault();
                          toast.error("You need to login first!");
                        } else {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                    >
                      <button className="btn-primary">Valuation</button>
                    </NavLink>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compare Cards Section */}
      <div className="compare-section">
        <div className="card">
          <img src={reitcard2} alt="Compare REITs" className="card-img" />
          <h3 className="card-title">Compare REITs</h3>
          <p className="card-desc">
            Compare financial metrics to make smarter choice
          </p>
          <button
            className="card-btn"
            onClick={() => {
              if (handleProtectedClick()) {
                handleArrowClick();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>

        <div className="card">
          <img
            src={reitcard1}
            alt="Compare REIT Properties"
            className="card-img"
          />
          <h3 className="card-title">Compare REITs Properties</h3>
          <p className="card-desc">
            Uncover property strength across locations and scale
          </p>
          <button
            className="card-btn"
            onClick={() => {
              if (handleProtectedClick()) {
                handleCompareClick();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
